/**
 * 复制文本到剪贴板。
 *
 * 策略（兼容线上 http://IP 等非 HTTPS）：
 * 1. 视口内 textarea 选区 + copy 事件里 setData（避免 execCommand 返回 true 但剪贴板为空）。
 * 2. 若 copy 事件未实际写入，再回退纯选区 execCommand。
 * 3. 安全上下文再调用 clipboard.writeText 双写加固。
 * 4. 任一可靠路径成功即可；全部失败才抛错。
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  const value = text.trim();
  if (!value) {
    throw new Error("empty-clipboard-text");
  }

  let eventOk: boolean;
  let selectionOk = false;

  try {
    eventOk = copyTextWithCopyEvent(value);
  } catch {
    eventOk = false;
  }

  if (!eventOk) {
    try {
      copyTextWithSelectionExecCommand(value);
      selectionOk = true;
    } catch {
      selectionOk = false;
    }
  }

  const canUseClipboardApi =
    typeof window !== "undefined" &&
    window.isSecureContext &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.clipboard?.writeText);

  if (canUseClipboardApi) {
    try {
      await navigator.clipboard!.writeText(value);
      return;
    } catch {
      if (eventOk || selectionOk) {
        return;
      }
      throw new Error("clipboard-copy-failed");
    }
  }

  if (eventOk || selectionOk) {
    return;
  }
  throw new Error("clipboard-copy-failed");
}

/**
 * 在 copy 事件中强制写入 text/plain。
 * 这是非 HTTPS 下对抗「execCommand 假成功」的主路径。
 */
function copyTextWithCopyEvent(text: string): boolean {
  if (typeof document === "undefined") {
    throw new Error("clipboard-unavailable");
  }

  let written = false;
  const onCopy = (event: Event) => {
    const clipboardEvent = event as ClipboardEvent;
    if (!clipboardEvent.clipboardData) {
      return;
    }
    clipboardEvent.clipboardData.setData("text/plain", text);
    clipboardEvent.preventDefault();
    written = true;
  };

  const textarea = createViewportTextarea(text);
  const previousActive = document.activeElement as HTMLElement | null;
  document.addEventListener("copy", onCopy);
  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    if (!document.execCommand("copy") || !written) {
      throw new Error("clipboard-copy-event-failed");
    }
    return true;
  } finally {
    document.removeEventListener("copy", onCopy);
    textarea.remove();
    previousActive?.focus?.({ preventScroll: true });
  }
}

/** 纯选区 + execCommand 回退路径。 */
function copyTextWithSelectionExecCommand(text: string) {
  if (typeof document === "undefined") {
    throw new Error("clipboard-unavailable");
  }

  const textarea = createViewportTextarea(text);
  const previousActive = document.activeElement as HTMLElement | null;
  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    if (!document.execCommand("copy")) {
      throw new Error("clipboard-copy-failed");
    }
  } finally {
    textarea.remove();
    previousActive?.focus?.({ preventScroll: true });
  }
}

/**
 * 创建必须留在视口内的临时输入框。
 * 不可使用 left:-9999px / display:none：部分 Chromium 会假成功且剪贴板为空。
 */
function createViewportTextarea(text: string): HTMLTextAreaElement {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("aria-hidden", "true");
  textarea.tabIndex = -1;
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.padding = "0";
  textarea.style.margin = "0";
  textarea.style.border = "0";
  textarea.style.outline = "none";
  // 必须可读可选：opacity:0 / 离屏定位会在部分 Chromium 假成功。
  textarea.style.opacity = "0.01";
  textarea.style.color = "transparent";
  textarea.style.caretColor = "transparent";
  textarea.style.background = "transparent";
  textarea.style.whiteSpace = "pre";
  textarea.style.overflow = "hidden";
  textarea.style.zIndex = "2147483647";
  textarea.style.fontSize = "12pt";
  document.body.appendChild(textarea);
  return textarea;
}
